import { useEffect, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useRedeemMapleKey } from '@/hooks/supabase/useRedeemMapleKey';

interface AddMapleKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AddMapleKeyDialog({ open, onOpenChange }: AddMapleKeyDialogProps) {
  const { toast } = useToast();
  const { redeem, redeeming } = useRedeemMapleKey();
  const [keyValue, setKeyValue] = useState('');

  useEffect(() => {
    if (!open) {
      setKeyValue('');
    }
  }, [open]);

  const handleRedeem = async () => {
    try {
      const result = await redeem(keyValue);
      toast({
        title: 'Maple Key redeemed',
        description: result.message,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Unable to redeem Maple Key',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            Add Maple Key
          </DialogTitle>
          <DialogDescription>
            Enter a Maple Key to redeem a full product license and add it to your Maple Market library.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="maple-key-input">Maple Key</Label>
            <Input
              id="maple-key-input"
              value={keyValue}
              onChange={(event) => setKeyValue(event.target.value)}
              placeholder="123e4567-e89b-12d3-a456-426614174000"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Valid keys grant immediate product ownership.
          </div>

          <Button type="button" className="w-full" onClick={() => void handleRedeem()} disabled={redeeming || !keyValue.trim()}>
            {redeeming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            Redeem Maple Key
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
